import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CommonProperties } from '../../../common/common.interfaces'
import { WEBHOOKS_FLEXPORT_STATUS_OPTIONS, WebhooksFlexportTableRow } from '../interfaces/WebhooksFlexportTableRow.interfaces'
import { DateTime } from 'luxon'
import { useLoading } from '../../../loading/LoadingContext'
import { useUserContext } from '../../../user/UserContext'
import { apiFetch } from '../../../utility/fetch'
import { API_DEV_URL, API_PROD_URL } from '../../..'
import RefreshSvg from '../../../common/components/svg/RefreshSvg'
import { Accordion, Button, Form } from 'react-bootstrap'
import BlobModalComponent from '../../../blobs/BlobModalComponent'
import JSONPretty from 'react-json-pretty'
import { CUSTOM_JSON_THEME } from '../../../common/common.constants'
import useCustomPagination from '../../../common/components/CustomPagination'
import { MILESTONES_FLEXPORT_DEFAULT } from '../FlexportMilestones.defaults'
import '../styles/FlexportMilestones.css'
import FlexportMilestoneSelector from './FlexportMilestoneSelector'
import { WebhookMilestonesFlexportTableRow } from '../interfaces/WebhookMilestonesFlexportTableRow.interfaces'
import { Eye, Search, Wrench } from 'react-bootstrap-icons'
import { FlexportMilestoneModal, FlexportMilestoneModalProps } from './FlexportMilestoneModal'
import HamburgerSvg from '../../../common/components/svg/HamburgerSvg'
import { notify } from '../../../notify/notify'
import { EnvironmentSelection } from '../../../common/components/EnvironmentSelection'

interface FlexportMilestoneEventsProps extends CommonProperties {}

export function FlexportMilestoneEvents(props: FlexportMilestoneEventsProps){
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const {userData} = useUserContext()
  const [environment, setEnvironment] = useState<string>('production')

  const [lookupType, setLookupType] = useState<string>('')
  const [rows, setRows] = useState<WebhooksFlexportTableRow[]>([])
  const [milestoneRows, setMilestoneRows] = useState<WebhookMilestonesFlexportTableRow[]>([])
  const [filteredRows, setFilteredRows] = useState<WebhooksFlexportTableRow[]>([])
  const [updateRows, setUpdateRows] = useState<{partitionKey: string, rowKey: string, status: string}[]>([])
  const [refresh, setRefresh] = useState<string>(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  
  const [modalRow, setModalRow] = useState<string>(null)
  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [modalTitle, setModalTitle] = useState<string>(null)
  const [modalContent, setModalContent] = useState<any>(null)
  const [modalRaw, setModalRaw] = useState<string>(null)

  const tableRef = useRef<HTMLDivElement>(null)
  const [activeKey, setActiveKey] = useState<string | null>(null)

  const [milestoneModal, setMilestoneModal] = useState<FlexportMilestoneModalProps>(null)

  const groupedMilestones = useMemo(() => {
    const groups: Record<string, typeof MILESTONES_FLEXPORT_DEFAULT> = {}
    for (const row of MILESTONES_FLEXPORT_DEFAULT) {
      const key = row.type?.split('#')[0]?.replace(/^\//, '') ?? 'unknown'
      if (!groups[key]) groups[key] = []
      groups[key].push(row)
    }
    return groups
  }, [])

  const {paginatedItems, paginationControls} = useCustomPagination<WebhooksFlexportTableRow>({
    sourceItems: filteredRows.length > 0 ? filteredRows : rows,
    pageSize: 50
  })

  const {paginatedItems: shipmentPaginatedItems, paginationControls: shipmentPaginationControls} = useCustomPagination<WebhookMilestonesFlexportTableRow>({
    sourceItems: groupedMilestones['shipment'],
    pageSize: 15
  })

  function refreshScreen(){
    setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  } 
  
  const loadTableRows = async function(){
    changeStep('Loading Flexport Webhook Events')
    changeSubStep('Getting rows from the webhooksFlexport table.')
    toggleLoading(true)
    const eventsResponse = await apiFetch<WebhooksFlexportTableRow[]>({
      endpoint: 'azureTables',
      method: 'GET',
      accessToken: userData.accessToken,
      baseUrlOverride: environment === 'production' ? API_PROD_URL : API_DEV_URL,
      params: [
        {key: 'table', value: 'webhooksFlexport'},
        {key: 'filter', value: `PartitionKey eq '${encodeURIComponent(lookupType)}'`}
      ]
    })
    const milestonesResponse = await apiFetch<WebhookMilestonesFlexportTableRow[]>({
      endpoint: 'azureTables',
      method: 'GET',
      accessToken: userData.accessToken,
      baseUrlOverride: environment === 'production' ? API_PROD_URL : API_DEV_URL,
      params: [
        {key: 'table', value: 'webhookMilestonesFlexport'}
      ]
    })
    setRows(eventsResponse.body)
    setUpdateRows([])
    setMilestoneRows(milestonesResponse.body)
    toggleLoading(false)
  }

  useEffect(() => {
    loadTableRows()
  }, [environment, refresh, lookupType])

  const handleShowEventData = useCallback(async (e) => {
    const {rkey} = e.currentTarget.dataset
    if(modalRow === rkey){
      //show already loaded content
      setModalVisible(true)
      return
    }

    changeStep('Loading Event Data')
    changeSubStep('Getting webhook event data from Azure.')
    toggleLoading(true)
    const row = rows.find(r => r.rowKey === rkey)
    const response = await apiFetch({
      endpoint: 'blob',
      method: 'GET',
      accessToken: userData.accessToken,
      baseUrlOverride: environment === 'production' ? API_PROD_URL : API_DEV_URL,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'flexport'},
        {key: 'blobname', value: row.dataPath}
      ]
    })

    setModalContent(<JSONPretty data={response.body} theme={CUSTOM_JSON_THEME} style={{fontSize: '1.5em'}} />)
    setModalRaw(JSON.stringify(response.body))
    setModalRow(rkey)
    setModalTitle(`${row.type} ${rkey} ${DateTime.fromISO(row.occurred_at).setZone('America/Chicago').toFormat('MM/dd/yy t')}`)
    setModalVisible(true)
    toggleLoading(false)
  }, [rows, environment, modalRow])

  const handleShowSalesforceData = useCallback((e) => {
    const {rkey} = e.currentTarget.dataset
    const row = rows.find(r => r.rowKey === rkey)
    setModalContent(<JSONPretty data={JSON.parse(row.salesforceRequest)} theme={CUSTOM_JSON_THEME} style={{fontSize: '1.5em'}} />)
    setModalRaw(row.salesforceRequest)
    setModalRow(rkey)
    setModalTitle(`${row.type} ${rkey} SALESFORCE REQUEST ${DateTime.fromISO(row.loadedOn).setZone('America/Chicago').toFormat('MM/dd/yy t')}`)
    setModalVisible(true)
  }, [rows, environment])

  const handleStatusChanged = useCallback((e) => {
    const { rkey } = e.currentTarget.dataset
    const value = e.currentTarget.value

    const source = rows.find(r => r.rowKey === rkey)
    if (!source) return

    setUpdateRows(prev => {
      const existingIndex = prev.findIndex(ur => ur.rowKey === rkey)

      // If reverted to original, remove from updates
      if (source.status === value) {
        if (existingIndex !== -1) {
          return [...prev.slice(0, existingIndex), ...prev.slice(existingIndex + 1)]
        }
        return prev
      }

      const updatedEntry = {
        partitionKey: source.partitionKey,
        rowKey: source.rowKey,
        status: value
      }

      if (existingIndex !== -1) {
        // Replace existing
        return [
          ...prev.slice(0, existingIndex),
          updatedEntry,
          ...prev.slice(existingIndex + 1)
        ]
      }

      // Add new entry
      return [...prev, updatedEntry]
    })
  }, [rows])

  const handleAccordionSelect = (eventKey: string | string[] | null) => {
    // Normalize to string (Accordion only uses string keys unless you're doing multi-select manually)
    if (typeof eventKey === 'string' || eventKey === null) {
      setActiveKey(eventKey as any)
    }
  }

  const handleMilestoneConfigSave = useCallback(async (webhook: WebhooksFlexportTableRow) => {
    changeStep('Saving Milestone Configuration')
    changeSubStep(`Saving ${webhook.type} configuration to the webhookMilestonesFlexport table.`)
    toggleLoading(true)
    const response = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: userData.accessToken,
      baseUrlOverride: environment === 'development' ? API_DEV_URL : API_PROD_URL,
      body: JSON.stringify({
        table: 'webhookMilestonesFlexport',
        records: [webhook]
      }) 
    })

    if(!response.ok){
      console.log(response.body)
      notify('error', `Failed to save webhook configuration for ${webhook.type}. See console error.`)
      toggleLoading(false)
      return
    }

    notify('success', `Webhook configuration saved!`)
    refreshScreen()

    toggleLoading(false)
  }, [environment])

  const handleSaveAll = useCallback(async (e) => {
    changeStep('Saving Event Statuses')
    changeSubStep(`Updating statuses for ${updateRows.length} event(s).`)
    toggleLoading(true)
    console.log(updateRows)
    const response = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: userData.accessToken,
      baseUrlOverride: environment === 'development' ? API_DEV_URL : API_PROD_URL,
      body: JSON.stringify({
        table: 'webhooksFlexport',
        records: updateRows
      })
    })
    if(!response.ok){
      console.error(response.body)
      notify('error', `Could not update the statuses for these rows. See console for more info.`)
      toggleLoading(false)
      return
    }

    notify('success', 'Succesfully updated statuses for the rows.')
    refreshScreen()

    toggleLoading(false)
  }, [updateRows, environment])

  return <div>
    <FlexportMilestoneModal 
      show={milestoneModal?.show}
      header={milestoneModal?.header}
      milestone={milestoneModal?.milestone}
      onHide={milestoneModal?.onHide}
      onSave={milestoneModal?.onSave}
    />
    <BlobModalComponent 
      blobData={modalContent}
      blobName={modalTitle}
      closeModal={() => setModalVisible(false)}
      modalVisible={modalVisible}
      raw={modalRaw}
    />
    {props?.isAdmin === true && <div>
      <div className='beacon-card'>
        <span className='beacon-header'>ADMINISTRATOR VIEW <Eye /></span>
        <br/>
        <span className='beacon-label'>Environment Selection</span>
        <br/>
        <EnvironmentSelection environment={environment} setEnvironment={setEnvironment} />
      </div>
    </div>}
    <div>
      <span className='beacon-label'>Select an event type to lookup. View all of the milestones here on their API <a href='https://apidocs.flexport.com/v3/tag/Milestones' target="_blank" rel="noopener noreferrer">documentation</a>.</span>
      <FlexportMilestoneSelector 
        milestones={MILESTONES_FLEXPORT_DEFAULT}
        onSelect={setLookupType}
        content={lookupType}
      />
    </div>
    <br/>
    <Accordion className='custom-accordion'>
      <Accordion.Item eventKey='0'>
        <Accordion.Header className='sticky-row-2'>
          <span className='beacon-header'>FLEXPORT WEBHOOK STATUSES</span>
        </Accordion.Header>
        <Accordion.Body>
          <table className='flexport-table'>
            <thead>
              <tr>
                <th>
                  STATUS
                </th>
                <th>
                  ENUM
                </th>
                <th>
                  DESCRIPTION
                </th>
              </tr>
            </thead>
            <tbody>
              {WEBHOOKS_FLEXPORT_STATUS_OPTIONS.map(option => (
                <tr>
                  <td>
                    {option.display}
                  </td>
                  <td>
                    <code>{option.value}</code>
                  </td>
                  <td>
                    {option.description}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>

    <Accordion className='custom-accordion' activeKey={activeKey} onSelect={handleAccordionSelect}>
      <Accordion.Item eventKey='0'>
        <Accordion.Header className='sticky-row-2'>
          <span className='beacon-header'>FLEXPORT WEBHOOK MILESTONES</span>
        </Accordion.Header>
        <Accordion.Body>
          <div>
            {Object.entries(groupedMilestones).map(([groupName, milestones]) => (
              <div key={groupName}>
                <Accordion>
                  <Accordion.Item eventKey={groupName}>
                    <Accordion.Header className='sticky-row-2'>
                      <span className='beacon-header'>{groupName.toUpperCase().replaceAll('_', ' ')} [{milestones.length}]</span>
                    </Accordion.Header>
                    <Accordion.Body>
                      {groupName === 'shipment' ? shipmentPaginationControls : null}
                      <table className='flexport-table'>
                        <thead>
                          <tr>
                            <th>EVENT</th>
                            <th>DESCRIPTION</th>
                            <th>ACTIONS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(groupName === 'shipment' ? shipmentPaginatedItems : milestones).map(r => {
                            const milestone = milestoneRows.find(mr => mr.type === r.type)
                            return <tr>
                              <td>{milestone && <span title='Configuration exists.' style={{color: 'darkgreen'}} className='beacon-label'><Wrench size={20} /></span>} <code>{r.type}</code></td>
                              <td>{r.description}</td>
                              <td>
                                <Button variant='secondary' onClick={() => {setMilestoneModal({
                                  header: <span>MANAGE CONFIGURATION - <code>{r.type}</code></span>,
                                  milestone: milestone ?? MILESTONES_FLEXPORT_DEFAULT.find(d => d.type === r.type),
                                  onHide: () => {setMilestoneModal({show: false})},
                                  show: true,
                                  onSave: handleMilestoneConfigSave
                                })}}> <HamburgerSvg height={20} /> MANAGE CONFIG</Button>
                                &nbsp;<Button variant='outline-secondary'
                                  onClick={() => {
                                    setLookupType(r.type)
                                    setActiveKey(null)
                                    setTimeout(() => {
                                      tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                                    }, 100)
                                  }}
                                ><Search size={20} /> SEARCH EVENTS</Button>
                              </td>
                            </tr>
                          })}
                        </tbody>
                      </table>
                      {groupName === 'shipment' ? shipmentPaginationControls : null}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </div>
            ))}
          </div>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
    <br/>
    <span className='beacon-header'>EVENTS</span>
    <br/>
    <span style={{cursor: 'pointer'}} onClick={refreshScreen}>
      <RefreshSvg height={25} />
    </span>&nbsp;
    <span className='beacon-label'>
      {rows?.length?.toLocaleString() ?? 0} row(s) loaded for
    </span>&nbsp;&nbsp;
    <span className='beacon-header'>{lookupType}</span>
    <br/>
    <span>
      <Button onClick={() => {setUpdateRows([]); refreshScreen();}} disabled={updateRows.length === 0} variant='outline-secondary' style={{width: '150px'}}>CLEAR ALL [{updateRows.length}] UPDATES</Button>&nbsp;&nbsp;
      <Button onClick={handleSaveAll} disabled={updateRows.length === 0} variant='outline-success' style={{width: '150px'}}>SAVE ALL [{updateRows.length}] UPDATES</Button>
    </span>
    {paginationControls}
    <div ref={tableRef}>
      <table className='flexport-table'>
        <thead className='sticky-row-2'>
          <tr>
            <th>TYPE</th>
            <th>EVENT ID</th>
            <th>STATUS</th>
            <th>OCCURRED ON</th>
            <th>LOADED ON</th>
            <th>NOTIFIED ON</th>
            <th>ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          {paginatedItems?.map(r => (
            <tr>
              <td><span className='beacon-header'><code>{r.type}</code></span></td>
              <td>{r.rowKey}</td>
              <td>
                <Form.Select
                  data-rkey={r.rowKey}
                  onChange={handleStatusChanged}
                  value={r.status}
                  style={{ height: '30px' }}
                >
                  {WEBHOOKS_FLEXPORT_STATUS_OPTIONS.map(option => (
                    <option
                      key={option.value}
                      value={option.value}
                      style={{ textAlign: 'center' }}
                      title={`${option.value}: ${option.description}`}
                    >
                      {option.display.toUpperCase()}
                    </option>
                  ))}
                </Form.Select>
                {updateRows.find(ur => ur.rowKey === r.rowKey) && <span className='beacon-label' style={{fontSize: '0.8em', color: 'darkgreen'}}>
                  SAVE CHANGES TO APPLY STATUS CHANGE TO <code style={{fontSize: '1.2rem'}}>{updateRows.find(ur => ur.rowKey === r.rowKey).status}</code>
                </span>}
              </td>
              <td>{DateTime.fromISO(r.occurred_at).setZone('America/Chicago').toFormat('MM/dd/yy t')}</td>
              <td>
                {r.loadedOn ? DateTime.fromISO(r.loadedOn).setZone('America/Chicago').toFormat('MM/dd/yy t') : 'N/A'}
              </td>
              <td>
                {r.notifiedOn ? DateTime.fromISO(r.notifiedOn).setZone('America/Chicago').toFormat('MM/dd/yy t') : 'N/A'}
              </td>
              <td>
                <Button title={`View data in ${r.dataPath}`} onClick={handleShowEventData} disabled={!r.dataPath} data-rkey={r.rowKey} style={{width: '100%'}} variant='secondary'>VIEW EVENT DATA</Button><br/>
                <Button onClick={handleShowSalesforceData} disabled={!r.salesforceRequest} data-rkey={r.rowKey} style={{width: '100%'}} variant='secondary'>VIEW SALESFORCE REQUEST</Button>
                <Button onClick={handleSaveAll} title='Saves the changed status.' disabled={!updateRows.find(ur => ur.rowKey === r.rowKey)} data-rkey={r.rowKey} style={{width: '100%'}} variant='success'>SAVE ROW STATUS</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {paginationControls}
  </div>
}